import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, Circle, Award, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CoursePlayerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourse();
  }, [id]);

  const fetchCourse = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API}/user/course/${id}/player`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourse(response.data);
      if (response.data.chapters && response.data.chapters.length > 0) {
        const firstIncomplete = response.data.chapters.find((ch) => !ch.completed);
        setCurrentChapter(firstIncomplete || response.data.chapters[0]);
      }
    } catch (error) {
      console.error("Error fetching course:", error);
      toast.error(error.response?.data?.detail || "Failed to load course");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const markChapterComplete = async (chapterId, completed) => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.post(
        `${API}/user/progress`,
        {
          course_id: id,
          chapter_id: chapterId,
          completed,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Update local state
      setCourse((prev) => ({
        ...prev,
        chapters: prev.chapters.map((ch) =>
          ch.id === chapterId ? { ...ch, completed } : ch
        ),
      }));

      if (completed) {
        toast.success("Chapter marked as complete!");
        
        // Check if course is completed
        if (response.data.course_completed) {
          toast.success("Congratulations! Course completed!");
          handleGenerateCertificate();
        }
      }
    } catch (error) {
      toast.error("Failed to update progress");
    }
  };

  const handleGenerateCertificate = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.post(`${API}/user/generate-certificate/${id}`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Certificate generated! Check your dashboard.");
    } catch (error) {
      console.error("Error generating certificate:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="w-16 h-16 border-4 border-[#0f3e3e] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!course || !currentChapter) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <p>Course not found</p>
        </div>
      </div>
    );
  }

  const completedChapters = course.chapters?.filter((ch) => ch.completed).length || 0;
  const totalChapters = course.chapters?.length || 0;
  const progressPercentage = totalChapters > 0 ? (completedChapters / totalChapters) * 100 : 0;

  return (
    <div className="min-h-screen bg-white" data-testid="course-player-page">
      <Navbar />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="mb-6"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0f3e3e] mb-2" data-testid="player-course-title">
          {course.title}
        </h1>

        <div className="mb-8">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Course Progress</span>
            <span className="font-medium text-[#0f3e3e]">
              {completedChapters} of {totalChapters} chapters
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video Player */}
          <div className="lg:col-span-2">
            <Card className="overflow-hidden">
              <div className="video-player-container" data-testid="video-player">

                <iframe
        width="100%"
        height="100%"
        src={currentChapter.video_url}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title="Embedded youtube"
      />
                
              </div>
              <div className="p-6">
                <h2 className="text-2xl font-semibold text-[#0f3e3e] mb-2" data-testid="current-chapter-title">
                  {currentChapter.title}
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Duration: {currentChapter.duration}
                </p>
                <Button
                  onClick={() => markChapterComplete(currentChapter.id, !currentChapter.completed)}
                  className={`${
                    currentChapter.completed
                      ? "bg-muted text-foreground hover:bg-muted/80"
                      : "bg-[#0f3e3e] text-white hover:bg-[#0f3e3e]/90"
                  }`}
                  data-testid="mark-complete-button"
                >
                  {currentChapter.completed ? (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Completed
                    </>
                  ) : (
                    <>
                      <Circle className="mr-2 h-4 w-4" />
                      Mark as Complete
                    </>
                  )}
                </Button>
              </div>
            </Card>
          </div>

          {/* Chapter List */}
          <div>
            <Card className="p-6">
              <h3 className="text-xl font-semibold text-[#0f3e3e] mb-4">Course Content</h3>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {course.chapters?.map((chapter, index) => (
                  <button
                    key={chapter.id}
                    onClick={() => setCurrentChapter(chapter)}
                    className={`w-full text-left p-3 rounded-md transition-colors ${
                      currentChapter.id === chapter.id
                        ? "bg-[#d4ff32]/20 border-l-3 border-[#0f3e3e]"
                        : "hover:bg-muted"
                    }`}
                    data-testid={`chapter-list-item-${chapter.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {chapter.completed ? (
                        <CheckCircle className="w-5 h-5 text-[#0f3e3e] flex-shrink-0 mt-0.5" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className={`font-medium ${chapter.completed ? "text-[#0f3e3e]" : "text-foreground"}`}>
                          {index + 1}. {chapter.title}
                        </p>
                        <p className="text-xs text-muted-foreground">{chapter.duration}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePlayerPage;
